import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tabs,
  Tab,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  useMediaQuery,
  useTheme,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  ArrowBack,
  Visibility,
  VisibilityOff,
  ExitToApp,
  AdminPanelSettings,
  Movie,
  Search,
  PlayArrow,
  Link,
} from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getAnimalAvatar, getDisplayUsername } from "../utils/userUtils";
import { getAllMoviesForAdmin, hideMovie, showMovie } from "../api/adminApi";

// Academic Theme
const academicTheme = createTheme({
  palette: {
    primary: {
      main: "#1565C0",
    },
    secondary: {
      main: "#FF8F00",
    },
    background: {
      default: "#F8F9FA",
    },
  },
  typography: {
    fontFamily: "'Kanit', 'Roboto', sans-serif",
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
});

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [movies, setMovies] = useState([]);
  const [hiddenMovies, setHiddenMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    movie: null,
    action: null,
  });
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    severity: "success",
  });

  // Pagination states
  const [visiblePage, setVisiblePage] = useState(0);
  const [hiddenPage, setHiddenPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Search states
  const [searchTerm, setSearchTerm] = useState("");

  // Reset pagination when search term changes
  useEffect(() => {
    setVisiblePage(0);
    setHiddenPage(0);
  }, [searchTerm]);

  const username = user?.username || "admin";
  const displayName = getDisplayUsername(username, "student_id");
  const animalAvatar = getAnimalAvatar(username);

  // Filter functions for search
  const getFilteredMovies = (moviesList) => {
    return moviesList.filter((movie) =>
      movie.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredVisibleMovies = getFilteredMovies(movies);
  const filteredHiddenMovies = getFilteredMovies(hiddenMovies);

  const loadMovies = useCallback(async () => {
    try {
      setLoading(true);
      // ใช้ API function
      const allMovies = await getAllMoviesForAdmin();
      console.log("Loaded movies:", allMovies); // Debug log
      setMovies(allMovies.filter((movie) => !movie.isHidden));
      setHiddenMovies(allMovies.filter((movie) => movie.isHidden));
    } catch (error) {
      console.error("Error loading movies:", error);
      showAlert("เกิดข้อผิดพลาดในการโหลดข้อมูล", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const showAlert = (message, severity) => {
    setAlert({ show: true, message, severity });
    setTimeout(
      () => setAlert({ show: false, message: "", severity: "success" }),
      3000
    );
  };

  // ตรวจสอบสิทธิ์ admin (เฉพาะ smoker8888)
  useEffect(() => {
    if (username !== "smoker8888") {
      navigate("/courses");
      return;
    }
    loadMovies();
  }, [username, navigate, loadMovies]);

  const handleMovieAction = async (movie, action) => {
    try {
      // ใช้ _id หรือ id ตามที่มีอยู่
      const movieId = movie._id || movie.id;

      if (!movieId) {
        showAlert("ไม่พบ ID ของบทเรียน", "error");
        return;
      }

      if (action === "hide") {
        await hideMovie(movieId);
        setMovies((prev) => prev.filter((m) => (m._id || m.id) !== movieId));
        setHiddenMovies((prev) => [...prev, { ...movie, isHidden: true }]);
        showAlert(`ซ่อนบทเรียน "${movie.title}" เรียบร้อยแล้ว`, "success");
      } else {
        await showMovie(movieId);
        setHiddenMovies((prev) =>
          prev.filter((m) => (m._id || m.id) !== movieId)
        );
        setMovies((prev) => [...prev, { ...movie, isHidden: false }]);
        showAlert(`แสดงบทเรียน "${movie.title}" เรียบร้อยแล้ว`, "success");
      }
    } catch (error) {
      console.error("Error updating movie:", error);
      showAlert(`เกิดข้อผิดพลาด: ${error.message}`, "error");
    }

    setConfirmDialog({ open: false, movie: null, action: null });
  };

  const handleLogout = () => {
    logout();
    navigate("/academic");
  };

  // ฟังก์ชันสำหรับ copy link ไปดูหนัง
  const handleCopyMovieLink = async (movie) => {
    const movieUrl = `${window.location.origin}/learn/${movie._id || movie.id}`;

    try {
      await navigator.clipboard.writeText(movieUrl);
      showAlert(`คัดลอกลิงค์ "${movie.title}" เรียบร้อยแล้ว`, "success");
    } catch (error) {
      // Fallback สำหรับ browser ที่ไม่รองรับ clipboard API
      const textArea = document.createElement("textarea");
      textArea.value = movieUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      showAlert(`คัดลอกลิงค์ "${movie.title}" เรียบร้อยแล้ว`, "success");
    }
  };

  const MoviesTable = ({ movies, isHidden, page, setPage }) => {
    // Filter movies based on search term
    const filteredMovies = movies.filter((movie) =>
      movie.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Calculate pagination
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedMovies = filteredMovies.slice(startIndex, endIndex);

    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };

    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F5F5F5" }}>
              <TableCell sx={{ fontWeight: "bold", width: 120 }}>ภาพ</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>ชื่อบทเรียน</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
                สถานะ
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", textAlign: "center", minWidth: 200 }}
              >
                การดำเนินการ
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
                การจัดการ
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedMovies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    ไม่มีข้อมูล
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedMovies.map((movie) => (
                <TableRow
                  key={movie._id || movie.id}
                  sx={{
                    "&:nth-of-type(odd)": { backgroundColor: "#FAFAFA" },
                    opacity: isHidden ? 0.8 : 1,
                    border: isHidden ? "2px solid #FF8F00" : "none",
                  }}
                >
                  <TableCell>
                    <Box
                      component="img"
                      src={movie.poster || movie.thumbnail}
                      alt={movie.title}
                      sx={{
                        width: 80,
                        height: 120,
                        objectFit: "cover",
                        borderRadius: 2,
                        border: "2px solid #E0E0E0",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                    <Box
                      sx={{
                        width: 80,
                        height: 120,
                        borderRadius: 2,
                        border: "2px solid #E0E0E0",
                        backgroundColor: "#F5F5F5",
                        display: "none",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    >
                      <Movie sx={{ color: "#9E9E9E", fontSize: 32, mb: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        ไม่มีภาพ
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {movie.title}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <Chip
                      label={isHidden ? "ซ่อนอยู่" : "แสดงอยู่"}
                      color={isHidden ? "warning" : "success"}
                      size="small"
                      icon={isHidden ? <VisibilityOff /> : <Visibility />}
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<PlayArrow />}
                      onClick={() =>
                        navigate(`/learn/${movie._id || movie.id}`, {
                          state: { movie },
                        })
                      }
                      sx={{
                        textTransform: "none",
                        minWidth: "auto",
                        px: { xs: 1, sm: 2 },
                        mr: 1,
                      }}
                    >
                      {isMobile ? "" : "ดูหนัง"}
                    </Button>

                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<Link />}
                      onClick={() => handleCopyMovieLink(movie)}
                      sx={{
                        textTransform: "none",
                        minWidth: "auto",
                        px: { xs: 1, sm: 2 },
                      }}
                    >
                      {isMobile ? "" : "คัดลอกลิงค์"}
                    </Button>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <Button
                      size="small"
                      variant={isHidden ? "contained" : "outlined"}
                      color={isHidden ? "success" : "warning"}
                      startIcon={isHidden ? <Visibility /> : <VisibilityOff />}
                      onClick={() =>
                        setConfirmDialog({
                          open: true,
                          movie,
                          action: isHidden ? "show" : "hide",
                        })
                      }
                      sx={{ textTransform: "none" }}
                    >
                      {isHidden ? "แสดง" : "ซ่อน"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          <caption>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredMovies.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="แถวต่อหน้า:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} จาก ${count}`
              }
            />
          </caption>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <ThemeProvider theme={academicTheme}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <Typography>กำลังโหลด...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={academicTheme}>
      <Box sx={{ backgroundColor: "#F8F9FA", minHeight: "100vh" }}>
        {/* Header */}
        <AppBar position="static" sx={{ backgroundColor: "#1565C0" }}>
          <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => navigate("/courses")}
              sx={{ mr: { xs: 1, sm: 2 }, flexShrink: 0 }}
            >
              <ArrowBack />
            </IconButton>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexGrow: 1,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              {!isMobile && (
                <AdminPanelSettings sx={{ mr: 1, flexShrink: 0 }} />
              )}
              <Typography
                variant={isMobile ? "body1" : "h6"}
                sx={{
                  flexGrow: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {isMobile ? "จัดการบทเรียน" : "จัดการบทเรียน - ระบบแอดมิน"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              <Avatar
                sx={{
                  bgcolor: "#FF8F00",
                  mr: 1,
                  width: { xs: 32, sm: 40 },
                  height: { xs: 32, sm: 40 },
                }}
              >
                {animalAvatar}
              </Avatar>
              {!isMobile && (
                <Typography variant="body2" sx={{ color: "white", mr: 2 }}>
                  {displayName}
                </Typography>
              )}
              <IconButton
                onClick={handleLogout}
                sx={{
                  color: "white",
                  p: { xs: 0.5, sm: 1 },
                }}
              >
                <ExitToApp />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Alert */}
        {alert.show && (
          <Alert
            severity={alert.severity}
            sx={{ margin: 2 }}
            onClose={() =>
              setAlert({ show: false, message: "", severity: "success" })
            }
          >
            {alert.message}
          </Alert>
        )}

        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography variant="h4" sx={{ color: "#1565C0", mb: 2 }}>
              🎓 ระบบจัดการบทเรียน
            </Typography>
            <Typography variant="body1" color="text.secondary">
              จัดการการแสดงผลและซ่อนบทเรียนในระบบ
            </Typography>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
            <Tabs
              value={selectedTab}
              onChange={(e, newValue) => setSelectedTab(newValue)}
            >
              <Tab
                label={
                  searchTerm
                    ? `บทเรียนที่แสดง (${filteredVisibleMovies.length}/${movies.length})`
                    : `บทเรียนที่แสดง (${movies.length})`
                }
                icon={<Visibility />}
                iconPosition="start"
              />
              <Tab
                label={
                  searchTerm
                    ? `บทเรียนที่ซ่อน (${filteredHiddenMovies.length}/${hiddenMovies.length})`
                    : `บทเรียนที่ซ่อน (${hiddenMovies.length})`
                }
                icon={<VisibilityOff />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Search Field */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="ค้นหาบทเรียน..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "#666" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: "white",
                borderRadius: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover": {
                    "& > fieldset": {
                      borderColor: "#1565C0",
                    },
                  },
                  "&.Mui-focused": {
                    "& > fieldset": {
                      borderColor: "#1565C0",
                    },
                  },
                },
              }}
            />
          </Box>

          {/* Content */}
          {selectedTab === 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3, color: "#1565C0" }}>
                📚 บทเรียนที่แสดงในระบบ (
                {searchTerm
                  ? `${filteredVisibleMovies.length} จาก ${movies.length} รายการ`
                  : `${movies.length} รายการ`}
                )
              </Typography>
              {filteredVisibleMovies.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: "center" }}>
                  <Movie
                    sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    {searchTerm
                      ? `ไม่พบบทเรียนที่ตรงกับ "${searchTerm}"`
                      : "ไม่มีบทเรียนที่แสดงในขณะนี้"}
                  </Typography>
                </Paper>
              ) : (
                <MoviesTable
                  movies={movies}
                  isHidden={false}
                  page={visiblePage}
                  setPage={setVisiblePage}
                />
              )}
            </Box>
          )}

          {selectedTab === 1 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3, color: "#FF8F00" }}>
                🔒 บทเรียนที่ซ่อนอยู่ (
                {searchTerm
                  ? `${filteredHiddenMovies.length} จาก ${hiddenMovies.length} รายการ`
                  : `${hiddenMovies.length} รายการ`}
                )
              </Typography>
              {filteredHiddenMovies.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: "center" }}>
                  <VisibilityOff
                    sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    {searchTerm
                      ? `ไม่พบบทเรียนที่ตรงกับ "${searchTerm}"`
                      : "ไม่มีบทเรียนที่ซ่อนอยู่"}
                  </Typography>
                </Paper>
              ) : (
                <MoviesTable
                  movies={hiddenMovies}
                  isHidden={true}
                  page={hiddenPage}
                  setPage={setHiddenPage}
                />
              )}
            </Box>
          )}
        </Container>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onClose={() =>
            setConfirmDialog({ open: false, movie: null, action: null })
          }
        >
          <DialogTitle>
            {confirmDialog.action === "hide"
              ? "🔒 ซ่อนบทเรียน"
              : "👁️ แสดงบทเรียน"}
          </DialogTitle>
          <DialogContent>
            <Typography>
              คุณต้องการ{confirmDialog.action === "hide" ? "ซ่อน" : "แสดง"}
              บทเรียน
              <strong> "{confirmDialog.movie?.title}" </strong> หรือไม่?
            </Typography>
            {confirmDialog.action === "hide" && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                ⚠️ บทเรียนจะไม่แสดงในระบบสำหรับผู้ใช้ทั่วไป
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setConfirmDialog({ open: false, movie: null, action: null })
              }
            >
              ยกเลิก
            </Button>
            <Button
              onClick={() =>
                handleMovieAction(confirmDialog.movie, confirmDialog.action)
              }
              variant="contained"
              color={confirmDialog.action === "hide" ? "warning" : "success"}
            >
              ยืนยัน
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default AdminPage;
